'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Check, X, Zap, Building2, Star, ArrowRight } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

interface Plan {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  description: string;
  icon: React.ElementType;
  color: string;
  badge?: string;
  features: string[];
  limits: { operators: string; patients: string; calls: string; sms: string };
}

const plans: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 49,
    yearlyPrice: 39,
    description: 'Perfetto per laboratori piccoli che iniziano.',
    icon: Zap,
    color: 'bg-blue-50 text-blue-600',
    features: ['Fino a 3 operatori', 'Gestione pazienti base', 'Storico chiamate 30 giorni', 'SMS via Twilio', 'Report base', 'Supporto email'],
    limits: { operators: '3', patients: '500', calls: '1.000/mese', sms: '500/mese' },
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 149,
    yearlyPrice: 119,
    description: 'Per laboratori in crescita con esigenze avanzate.',
    icon: Star,
    color: 'bg-primary/10 text-primary',
    badge: 'Più Popolare',
    features: ['Fino a 15 operatori', 'Gestione pazienti avanzata', 'Storico illimitato', 'SMS + WhatsApp', 'Analytics avanzate', 'Automazione follow-up', 'Portale paziente', 'Cal.com integrato', 'Supporto prioritario'],
    limits: { operators: '15', patients: '5.000', calls: '10.000/mese', sms: '5.000/mese' },
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: 399,
    yearlyPrice: 319,
    description: 'Per reti di laboratori e grandi strutture.',
    icon: Building2,
    color: 'bg-amber-50 text-amber-600',
    features: ['Operatori illimitati', 'Multi-sede', 'API personalizzate', 'SLA garantito 99.9%', 'Onboarding dedicato', 'Account manager', 'SSO/SAML', 'Audit trail avanzato', 'Supporto 24/7'],
    limits: { operators: 'Illimitati', patients: 'Illimitati', calls: 'Illimitate', sms: 'Illimitati' },
  },
];

const allFeatures = [
  { label: 'Gestione chiamate', starter: true, pro: true, enterprise: true },
  { label: 'Gestione pazienti', starter: true, pro: true, enterprise: true },
  { label: 'Tracciamento esami', starter: true, pro: true, enterprise: true },
  { label: 'SMS via Twilio', starter: true, pro: true, enterprise: true },
  { label: 'Analytics avanzate', starter: false, pro: true, enterprise: true },
  { label: 'Automazione follow-up', starter: false, pro: true, enterprise: true },
  { label: 'Portale paziente', starter: false, pro: true, enterprise: true },
  { label: 'Cal.com integrato', starter: false, pro: true, enterprise: true },
  { label: 'API access', starter: false, pro: false, enterprise: true },
  { label: 'SSO/SAML', starter: false, pro: false, enterprise: true },
  { label: 'SLA garantito 99.9%', starter: false, pro: false, enterprise: true },
  { label: 'Account manager dedicato', starter: false, pro: false, enterprise: true },
];

export default function PricingPage() {
  const [yearly, setYearly] = useState(false);

  const handleCheckout = async (planId: string) => {
    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planId, billing: yearly ? 'yearly' : 'monthly' }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      window.location.href = '/register/lab';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-semibold text-sm text-foreground">LabCall<span className="text-primary">Manager</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <Link href="/call-dashboard" className="text-sm text-muted-foreground hover:text-foreground">Accedi</Link>
            <Link href="/register/lab" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">Inizia Gratis</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">Prezzi semplici e trasparenti</h1>
          <p className="text-lg text-muted-foreground mb-8">Scegli il piano giusto per il tuo laboratorio. Cambia o cancella in qualsiasi momento.</p>
          <div className="inline-flex items-center gap-3 bg-muted rounded-full p-1">
            <button onClick={() => setYearly(false)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${!yearly ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`}>Mensile</button>
            <button onClick={() => setYearly(true)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${yearly ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`}>
              Annuale <span className="text-green-600 text-xs font-semibold ml-1">-20%</span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {plans.map((plan) => (
            <div key={plan.id} className={`relative rounded-2xl border-2 p-8 ${plan.badge ? 'border-primary shadow-xl shadow-primary/10' : 'border-border'}`}>
              {plan.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-white text-xs font-semibold rounded-full">{plan.badge}</div>
              )}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${plan.color}`}>
                <plan.icon size={20} />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-1">{plan.name}</h3>
              <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-foreground">€{yearly ? plan.yearlyPrice : plan.monthlyPrice}</span>
                <span className="text-muted-foreground text-sm">/mese</span>
                {yearly && <p className="text-xs text-green-600 mt-1">Fatturato annualmente (€{plan.yearlyPrice * 12}/anno)</p>}
              </div>
              <button
                onClick={() => handleCheckout(plan.id)}
                className={`w-full py-3 rounded-xl font-semibold text-sm transition-colors mb-6 ${plan.badge ? 'bg-primary text-white hover:bg-primary/90' : 'bg-muted text-foreground hover:bg-muted/80'}`}
              >
                Inizia con {plan.name} <ArrowRight size={14} className="inline ml-1" />
              </button>
              <ul className="space-y-2.5">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check size={15} className="text-green-500 shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Feature comparison */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">Confronto completo delle funzionalità</h2>
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Funzionalità</th>
                  {plans.map((p) => <th key={p.id} className="px-6 py-4 text-sm font-semibold text-foreground text-center">{p.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {allFeatures.map((f, i) => (
                  <tr key={f.label} className={i % 2 === 0 ? 'bg-white' : 'bg-muted/10'}>
                    <td className="px-6 py-3.5 text-sm text-foreground">{f.label}</td>
                    <td className="px-6 py-3.5 text-center">{f.starter ? <Check size={16} className="text-green-500 mx-auto" /> : <X size={16} className="text-muted-foreground/40 mx-auto" />}</td>
                    <td className="px-6 py-3.5 text-center">{f.pro ? <Check size={16} className="text-green-500 mx-auto" /> : <X size={16} className="text-muted-foreground/40 mx-auto" />}</td>
                    <td className="px-6 py-3.5 text-center">{f.enterprise ? <Check size={16} className="text-green-500 mx-auto" /> : <X size={16} className="text-muted-foreground/40 mx-auto" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">Domande Frequenti</h2>
          <div className="space-y-4">
            {[
              { q: 'Posso cambiare piano in qualsiasi momento?', a: 'Sì, puoi fare upgrade o downgrade del tuo piano in qualsiasi momento. Le modifiche vengono applicate immediatamente.' },
              { q: 'Come funziona la prova gratuita?', a: '14 giorni di accesso completo al piano Pro, senza carta di credito. Al termine puoi scegliere il piano più adatto.' },
              { q: 'I dati sono al sicuro?', a: 'Sì. Utilizziamo crittografia end-to-end, backup automatici e siamo conformi al GDPR per la protezione dei dati sanitari.' },
              { q: 'Offrite assistenza per la migrazione?', a: 'Sì, per i piani Pro ed Enterprise offriamo supporto dedicato per importare i dati esistenti.' },
            ].map((faq) => (
              <div key={faq.q} className="p-5 rounded-xl border border-border">
                <h4 className="font-semibold text-foreground mb-2 text-sm">{faq.q}</h4>
                <p className="text-sm text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
